/* eslint-disable @typescript-eslint/no-explicit-any */
import { Action } from 'redux';

import { ThunkAction } from 'redux-thunk';
import { ErrorAction, OkAction } from './baseActions';

import { SlackChannel } from '~/types/resources';
import * as slackService from '../services/slackAPI';
import { formatApiErrorPayload } from './utils';
import { selectCurrentWorkspaceId } from './utils/selectors';

type AppState = import('~/store').AppState;

export enum SlackActionType {
  LINK_TO_SLACK = 'LINK_TO_SLACK',
  LINK_TO_SLACK_OK = 'LINK_TO_SLACK_OK',
  LINK_TO_SLACK_ERROR = 'LINK_TO_SLACK_ERROR',
  UNLINK_FROM_SLACK = 'UNLINK_FROM_SLACK',
  UNLINK_FROM_SLACK_OK = 'UNLINK_FROM_SLACK_OK',
  UNLINK_FROM_SLACK_ERROR = 'UNLINK_FROM_SLACK_ERROR',
  CHANGE_SLACK_CHANNELS_CONFIGURATION = 'CHANGE_SLACK_CHANNELS_CONFIGURATION',
  CHANGE_SLACK_CHANNELS_CONFIGURATION_OK = 'CHANGE_SLACK_CHANNELS_CONFIGURATION_OK',
  CHANGE_SLACK_CHANNELS_CONFIGURATION_ERROR = 'CHANGE_SLACK_CHANNELS_CONFIGURATION_ERROR',
  GET_SLACK_CHANNELS_CONFIGURATION = 'GET_SLACK_CHANNELS_CONFIGURATION',
  GET_SLACK_CHANNELS_CONFIGURATION_OK = 'GET_SLACK_CHANNELS_CONFIGURATION_OK',
  GET_SLACK_CHANNELS_CONFIGURATION_ERROR = 'GET_SLACK_CHANNELS_CONFIGURATION_ERROR',
  LIST_SLACK_CHANNELS = 'LIST_SLACK_CHANNELS',
  LIST_SLACK_CHANNELS_OK = 'LIST_SLACK_CHANNELS_OK',
  LIST_SLACK_CHANNELS_ERROR = 'LIST_SLACK_CHANNELS_ERROR',
  REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION = 'REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION',
  REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION_OK = 'REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION_OK',
  REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION_ERROR = 'REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION_ERROR',
  GET_SLACK_CHANNEL_INFO = 'GET_SLACK_CHANNEL_INFO',
  GET_SLACK_CHANNEL_INFO_OK = 'GET_SLACK_CHANNEL_INFO_OK',
  GET_SLACK_CHANNEL_INFO_ERROR = 'GET_SLACK_CHANNEL_INFO_ERROR',
}

type WithChannelIds = { successfulRunsChannelId?: string; failedRunsChannelId?: string };
type WithChannelUpdate = { notificationType: slackService.NotificationType; channelId: string };

type LinkToSlackAction =
  | Action<SlackActionType.LINK_TO_SLACK>
  | OkAction<SlackActionType.LINK_TO_SLACK_OK, { channels: SlackChannel[]; linked: boolean }>
  | ErrorAction<SlackActionType.LINK_TO_SLACK_ERROR>;

type UnlinkFromSlackAction =
  | Action<SlackActionType.UNLINK_FROM_SLACK>
  | OkAction<SlackActionType.UNLINK_FROM_SLACK_OK, any>
  | ErrorAction<SlackActionType.UNLINK_FROM_SLACK_ERROR>;

type ChangeSlackChannelAction =
  | (Action<SlackActionType.CHANGE_SLACK_CHANNELS_CONFIGURATION> &
      WithChannelIds &
      Pick<WithChannelUpdate, 'notificationType'>)
  | OkAction<SlackActionType.CHANGE_SLACK_CHANNELS_CONFIGURATION_OK, any>
  | ErrorAction<SlackActionType.CHANGE_SLACK_CHANNELS_CONFIGURATION_ERROR>;

type GetSlackChannelAction =
  | Action<SlackActionType.GET_SLACK_CHANNELS_CONFIGURATION>
  | OkAction<SlackActionType.GET_SLACK_CHANNELS_CONFIGURATION_OK, WithChannelIds>
  | ErrorAction<SlackActionType.GET_SLACK_CHANNELS_CONFIGURATION_ERROR>;

type RemoveSlackChannelAction =
  | (Action<SlackActionType.REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION> & WithChannelUpdate)
  | OkAction<SlackActionType.REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION_OK, WithChannelIds>
  | ErrorAction<SlackActionType.REMOVE_SLACK_CHANNEL_FROM_CONFIGURATION_ERROR>;

type ListSlackChannelsAction =
  | Action<SlackActionType.LIST_SLACK_CHANNELS>
  | OkAction<SlackActionType.LIST_SLACK_CHANNELS_OK, { channels: SlackChannel[]; linked: boolean }>
  | ErrorAction<SlackActionType.LIST_SLACK_CHANNELS_ERROR>;

type GetSlackChannelInfoAction =
  | Action<SlackActionType.GET_SLACK_CHANNEL_INFO>
  | OkAction<SlackActionType.GET_SLACK_CHANNEL_INFO_OK, any>
  | ErrorAction<SlackActionType.GET_SLACK_CHANNEL_INFO_ERROR>;

export type SlackLinkAction =
  | LinkToSlackAction
  | UnlinkFromSlackAction
  | ChangeSlackChannelAction
  | ListSlackChannelsAction
  | GetSlackChannelAction
  | RemoveSlackChannelAction
  | GetSlackChannelInfoAction;

export const linkToSlack =
  (link: { code: string; state: string }): ThunkAction<void, AppState, null, LinkToSlackAction> =>
  async (dispatch, getState) => {
    dispatch({ type: SlackActionType.LINK_TO_SLACK });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await slackService.linkToSlack({ workspaceId, link });
    if (response.ok && response.data) {
      dispatch({
        type: SlackActionType.LINK_TO_SLACK_OK,
        data: response.data,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: SlackActionType.LINK_TO_SLACK_ERROR,
      });
    }
  };

export const unlinkFromSlack =
  (): ThunkAction<void, AppState, null, UnlinkFromSlackAction> => async (dispatch, getState) => {
    dispatch({ type: SlackActionType.UNLINK_FROM_SLACK });
    const workspaceId = selectCurrentWorkspaceId(getState());

    // TODO I should also remove any selected channel from notifications
    const response = await slackService.unlinkFromSlack({ workspaceId });
    if (response.ok) {
      dispatch({
        type: SlackActionType.UNLINK_FROM_SLACK_OK,
        data: response.data,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: SlackActionType.UNLINK_FROM_SLACK_ERROR,
      });
    }
  };

export const listSlackChannels =
  (): ThunkAction<void, AppState, null, ListSlackChannelsAction> => async (dispatch, getState) => {
    dispatch({ type: SlackActionType.LIST_SLACK_CHANNELS });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await slackService.listSlackChannels({ workspaceId });
    if (response.ok && response.data) {
      dispatch({
        type: SlackActionType.LIST_SLACK_CHANNELS_OK,
        data: response.data,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: SlackActionType.LIST_SLACK_CHANNELS_ERROR,
      });
    }
  };

export const getSlackChannelInfo =
  (channelId: string): ThunkAction<void, AppState, null, GetSlackChannelInfoAction> =>
  async (dispatch, getState) => {
    dispatch({ type: SlackActionType.GET_SLACK_CHANNEL_INFO });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await slackService.getSlackChannelInfo({ workspaceId, channelId });
    if (response.ok) {
      dispatch({
        type: SlackActionType.GET_SLACK_CHANNEL_INFO_OK,
        data: response.data,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: SlackActionType.GET_SLACK_CHANNEL_INFO_ERROR,
      });
    }
  };
